import { Injectable, Renderer2, RendererFactory2, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ThemeName } from '@venecia/models';

import { StorageService, RouterService } from '@venecia/services';
import { GlobalConstants } from '@venecia/constants';

const THEME_LINK_ID = 'app-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {

    private readonly renderer: Renderer2;
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly routerService = inject(RouterService);

    readonly theme = signal<ThemeName>('light');

    constructor(
        rendererFactory: RendererFactory2,
        private readonly storage: StorageService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    initialize(): void {
        const appTheme = this.activatedRoute.snapshot.queryParams[GlobalConstants.THEME_KEY];

        if (appTheme === 'light' || appTheme === 'dark') {
            this.theme.set(appTheme);
        } else {
            this.theme.set(this.detectInitialTheme());
        }

        this.applyTheme(this.theme());
    }

    toggle(): void {
        this.theme.set(this.theme() === 'light' ? 'dark' : 'light');
        this.applyTheme(this.theme());
    }

    set(theme: ThemeName): void {
        this.theme.set(theme);
    }

    get(): ThemeName {
        return this.theme();
    }

    private detectInitialTheme(): ThemeName {
        if (this.storage.exists(GlobalConstants.THEME_KEY)) {
            const stored = this.storage.get<ThemeName>(GlobalConstants.THEME_KEY);
            if (stored === 'light' || stored === 'dark') {
                return stored;
            }
        }

        const systemTheme = this.getSystemTheme();
        if (systemTheme) {
            return systemTheme;
        }

        if (typeof document === 'undefined') {
            return 'light';
        }

        const dataTheme = document.documentElement.getAttribute('data-theme');
        if (dataTheme === 'light' || dataTheme === 'dark') {
            return dataTheme;
        }

        const link = document.getElementById(THEME_LINK_ID) as HTMLLinkElement | null;
        if (link?.href?.includes('theme-dark.css')) {
            return 'dark';
        }

        return 'light';
    }

    private getSystemTheme(): ThemeName | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
        return prefersDark ? 'dark' : 'light';
    }

    private applyTheme(theme: ThemeName): void {
        if (typeof document === 'undefined') {
            return;
        }

        this.storage.set<ThemeName>(GlobalConstants.THEME_KEY, theme);
        this.routerService.setQueryParams({
            [GlobalConstants.THEME_KEY]: theme as string,
        });
        document.documentElement.setAttribute('data-theme', theme);

        const href = `/themes/theme-${theme}.css`;
        const link = document.getElementById(THEME_LINK_ID) as HTMLLinkElement | null;

        if (!link) {
            // If link doesn't exist, create it
            const created = this.renderer.createElement('link') as HTMLLinkElement;
            this.renderer.setAttribute(created, 'id', THEME_LINK_ID);
            this.renderer.setAttribute(created, 'rel', 'stylesheet');
            this.renderer.setAttribute(created, 'href', href);
            this.renderer.appendChild(document.head, created);
            return;
        }

        if (link.href.endsWith(href)) {
            return;
        }

        // Preload new CSS to avoid FOUC
        const temp = this.renderer.createElement('link') as HTMLLinkElement;
        this.renderer.setAttribute(temp, 'rel', 'stylesheet');
        this.renderer.setAttribute(temp, 'href', href);

        temp.onload = () => {
            this.renderer.setAttribute(link, 'href', href);
            this.renderer.removeChild(document.head, temp);
        };

        this.renderer.appendChild(document.head, temp);
    }
}
